import { getWorkspace } from "@/db/workspaceDatabase";
import {
  WORKSPACE_LIBRARY_SERVICE_IDENT,
  type IWorkspaceLibraryService,
} from "@bloomstack/apis/workspace";
import {
  findStreamPageByEntryId,
  getStreamPageEntries,
} from "@/db/chatDatabase";
import {
  DATASTREAM_MANAGER_SERVICE_IDENT,
  type IDataStreamManagerService,
} from "@bloomstack/apis/services/dataStreamManager/IDataStreamManagerService";
import type { LLMHistory, LLMHistoryTextContent } from "@bloomstack/apis/llmTypes";
import { type IAgentServiceV2 } from "@bloomstack/apis/agentTypes";

/**
 * Resolve a workspace's active stream ID. Tries the Dexie workspace cache
 * first, then falls back to the workspace library service.
 */
export async function resolveStreamId(
  workspaceId: string,
  workspaceService: IWorkspaceLibraryService,
): Promise<string | null> {
  const cached = await getWorkspace(workspaceId);
  if (cached?.activeStreamId) return cached.activeStreamId;
  const res = await workspaceService.read(workspaceId);
  return res.success ? (res.result.activeStreamId ?? null) : null;
}

/**
 * Join an entry's visible text into one string. Only `type: "text"` content is
 * spoken; thinking is a separate type (already excluded) and `internal` text
 * (e.g. summarizer output) is hidden in the UI so it must not be spoken either.
 */
export function entryToText(entry: LLMHistory): string {
  return entry.content
    .filter((c): c is LLMHistoryTextContent => c.type === "text" && !c.internal)
    .map((c) => c.text)
    .join("");
}

/**
 * Resolve the full text of a completed entry. Dexie first (via page lookup),
 * backend fallback via fetchHistory if the entry isn't cached locally.
 */
export async function resolveEntryText(
  streamId: string,
  entryId: string,
  dataStreamService: IDataStreamManagerService,
): Promise<string | null> {
  // Dexie path
  const pageIndex = await findStreamPageByEntryId(streamId, entryId);
  if (pageIndex !== null) {
    const entries = await getStreamPageEntries(streamId, pageIndex);
    const entry = entries?.find((e) => e.id === entryId);
    if (entry) return entryToText(entry);
  }
  // Backend fallback: fetch history and locate the entry
  const res = await dataStreamService.fetchHistory(streamId, { stopEntryId: entryId });
  if (res.success) {
    const entry = res.result.entries.find((e) => e.id === entryId);
    if (entry) return entryToText(entry);
  }
  return null;
}

export type BlockEntry = { entryId: string; entry: LLMHistory };

/**
 * Resolve every entry that makes up one visual agent "turn" block.
 *
 * A single turn is a TREE of entries: the root entry (the one recorded in
 * StreamState.entryId), plus a NEW child entry created for each tool call
 * (parented to keep the turn as one message block — see brain5 handleToolUse).
 * Text emitted after a tool call lands on these child entries, NOT the root, so
 * speaking only the root would drop everything after the first tool call.
 *
 * Returns the root followed by its descendants (chained via parentId), ordered
 * by sequenceNum so speech follows the visual/chronological order. The chain
 * stops at the block boundary: the next agent turn starts a fresh root that is
 * not a descendant, so it is never included.
 */
export async function resolveBlockEntries(
  streamId: string,
  rootEntryId: string,
  dataStreamService: IDataStreamManagerService,
): Promise<BlockEntry[]> {
  // Gather a candidate pool of entries: prefer the Dexie page holding the root,
  // fall back to backend history. Both may be needed since child entries stream
  // in and might not be cached yet.
  const pool = new Map<string, LLMHistory>();

  const pageIndex = await findStreamPageByEntryId(streamId, rootEntryId);
  if (pageIndex !== null) {
    const entries = await getStreamPageEntries(streamId, pageIndex);
    for (const e of entries ?? []) if (e.id) pool.set(e.id, e);
  }
  if (!pool.has(rootEntryId)) {
    const res = await dataStreamService.fetchHistory(streamId, {});
    if (res.success) {
      for (const e of res.result.entries) if (e.id) pool.set(e.id, e);
    }
  }

  const root = pool.get(rootEntryId);
  if (!root) return [];

  // Collect the root plus every descendant reachable via parentId.
  const blockIds = new Set<string>([rootEntryId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const e of pool.values()) {
      if (e.id && e.parentId && blockIds.has(e.parentId) && !blockIds.has(e.id)) {
        blockIds.add(e.id);
        grew = true;
      }
    }
  }

  const block = [...blockIds]
    .map((id) => pool.get(id))
    .filter((e): e is LLMHistory => !!e)
    .sort((a, b) => (a.sequenceNum ?? 0) - (b.sequenceNum ?? 0));

  return block.map((entry) => ({ entryId: entry.id!, entry }));
}

/**
 * Resolve an agent's voice by userProfileId, caching results in the provided
 * Map. Returns null if the profile has no agent or no voice configured.
 */
export async function resolveVoice(
  userProfileId: string,
  cache: Map<string, string | null>,
  agentService: IAgentServiceV2,
): Promise<string | null> {
  if (cache.has(userProfileId)) return cache.get(userProfileId)!;
  const res = await agentService.readByUserProfileId(userProfileId);
  const voice = res.success ? (res.result.voice ?? null) : null;
  cache.set(userProfileId, voice);
  return voice;
}
